import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { useSiteData } from '@/context/SiteDataContext';
import { MediaUpload } from '@/components/MediaUpload';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Upload, X, Plus } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK_CATEGORIES = ['Plot', 'House', 'Commercial', 'Farmhouse'];
const VISIBILITY_FIELDS = [
  { key: 'show_total_price', label: 'Show Total Price' },
  { key: 'show_rate_per_sqft', label: 'Show Price / sq ft' },
  { key: 'show_gov_rate', label: 'Show Government Value' },
  { key: 'show_area', label: 'Show Area' },
  { key: 'show_price_range', label: 'Show Price Range' },
];

// Dropdown options
const statuses = ['Available', 'Booked', 'Sold', 'Coming Soon', 'Under Construction', 'Ready to Move'];
const listingTypes = ['Sell', 'Rent', 'Lease'];
const propertyTypes = ['Plot', 'House', 'Apartment', 'Commercial Shop', 'Office', 'Warehouse', 'Showroom', 'Other'];
const transactionTypes = ['New', 'Resale'];
const locations = ['Shivpuri', 'Jamul', 'Bhilai', 'Khedamara', 'Other'];
const bhkOptions = ['NA', '1 BHK', '2 BHK', '3 BHK', '4 BHK', '5+ BHK'];
const diversionTypes = ['NA', 'Diversion', 'Non Diversion'];
const plotCategories = ['Residential Plot', 'Commercial Plot', 'Agricultural', 'Farm Land'];
const facingOptions = ['North', 'South', 'East', 'West', 'North-East', 'North-West', 'South-East', 'South-West', 'Corner'];
const constructionStatuses = ['Under Construction', 'Ready to Move'];
const furnishingStatuses = ['Unfurnished', 'Semi Furnished', 'Fully Furnished'];
const waterSources = ['Municipal', 'Borewell', 'Both'];
const powerBackupOptions = ['None', 'Partial', 'Full'];
const commercialTypes = ['Shop', 'Office', 'Warehouse', 'Showroom', 'Godown', 'Clinic', 'Other'];
const zoneTypes = ['Commercial', 'Industrial', 'Mixed Use', 'Residential'];
const signageOptions = ['Main Road Facing', 'Corner', 'Inside Market', 'None'];
const ownerTypes = ['Builder', 'Owner', 'Broker'];

// Amenities grouped by category
const basicAmenities = [
  { key: 'roads_internal', label: 'Internal Roads' },
  { key: 'street_lights', label: 'Street Lights' },
  { key: 'drainage', label: 'Drainage System' },
  { key: 'electricity', label: 'Electricity Connection' },
  { key: 'water_supply', label: 'Water Supply' },
  { key: 'compound_wall', label: 'Compound Wall' },
  { key: 'parking', label: 'Parking Space' },
  { key: 'sewerage', label: 'Sewerage' },
];

const locationAmenities = [
  { key: 'nearby_market', label: 'Nearby Market' },
  { key: 'nearby_school', label: 'Nearby School' },
  { key: 'nearby_hospital', label: 'Nearby Hospital' },
  { key: 'nearby_bus_stop', label: 'Nearby Bus Stop' },
];

const securityAmenities = [
  { key: 'gated_campus', label: 'Gated Campus' },
  { key: 'security', label: '24/7 Security' },
  { key: 'security_24x7', label: 'Security Personnel' },
  { key: 'cctv', label: 'CCTV Surveillance' },
  { key: 'fire_safety', label: 'Fire Safety' },
  { key: 'intercom', label: 'Intercom' },
];

const premiumAmenities = [
  { key: 'park_play_area', label: 'Park/Play Area' },
  { key: 'clubhouse', label: 'Clubhouse' },
  { key: 'gym', label: 'Gym' },
  { key: 'swimming_pool', label: 'Swimming Pool' },
  { key: 'kids_play_area', label: 'Kids Play Area' },
  { key: 'garden', label: 'Garden' },
  { key: 'jogging_track', label: 'Jogging Track' },
  { key: 'indoor_games', label: 'Indoor Games' },
  { key: 'community_hall', label: 'Community Hall' },
];

const houseAmenities = [
  { key: 'modular_kitchen', label: 'Modular Kitchen' },
  { key: 'wardrobes', label: 'Wardrobes' },
  { key: 'false_ceiling', label: 'False Ceiling' },
  { key: 'geyser', label: 'Geyser' },
  { key: 'fans_lights', label: 'Fans & Lights' },
  { key: 'lift_available', label: 'Lift Available' },
  { key: 'water_storage', label: 'Water Storage' },
];

const commercialAmenities = [
  { key: 'pantry', label: 'Pantry' },
  { key: 'goods_lift', label: 'Goods Lift' },
  { key: 'loading_unloading_area', label: 'Loading/Unloading Area' },
  { key: 'loan_assistance', label: 'Loan Assistance' },
];

const initialFormData = {
  // Basic fields
  title: '',
  title_hi: '',
  code: '',
  category: 'Plot',
  status: 'Available',
  listing_type: 'Sell',
  property_type: '',
  transaction_type: 'New',
  
  // Location fields
  location_label: 'Shivpuri',
  full_address: '',
  state: 'Chhattisgarh',
  city: '',
  locality: '',
  landmark: '',
  pincode: '',
  latitude: '',
  longitude: '',
  google_map_link: '',
  
  // Pricing fields
  rate_per_sqft: '',
  gov_rate: '',
  total_price: '',
  pricing_note: '',
  negotiable: false,
  booking_amount: '',
  payment_plan: '',
  loan_possible: false,
  
  // Plot specific fields
  plot_area_sqft: '',
  plot_area_unit: 'sq ft',
  plot_category: '',
  diversion_type: 'NA',
  road_width_ft: '',
  plot_facing: '',
  plot_number: '',
  sector_block: '',
  
  // House specific fields
  house_area_sqft: '',
  carpet_area_sqft: '',
  builtup_area_sqft: '',
  super_builtup_area_sqft: '',
  bhk: 'NA',
  bathrooms: '',
  balconies: '',
  floor_info: '',
  floor_number: '',
  total_floors: '',
  property_age_years: '',
  construction_status: '',
  furnishing_status: '',
  flooring_type: '',
  ac_count: '',
  covered_parking: '',
  open_parking: '',
  water_source: '',
  power_backup: '',
  maintenance_per_month: '',
  
  // Commercial specific fields
  commercial_type: '',
  frontage_ft: '',
  ceiling_height_ft: '',
  zone_type: '',
  possession_status: '',
  available_from_date: '',
  monthly_rent: '',
  rent_negotiable: false,
  security_deposit: '',
  lock_in_period_months: '',
  annual_rent_increase_percent: '',
  lease_term_years: '',
  maintenance_included: false,
  washrooms_private: '',
  washrooms_common: '',
  signage_visibility: '',
  
  // Amenities (all booleans)
  roads_internal: false,
  street_lights: false,
  drainage: false,
  electricity: false,
  water_supply: false,
  compound_wall: false,
  parking: false,
  nearby_market: false,
  nearby_school: false,
  nearby_hospital: false,
  nearby_bus_stop: false,
  loan_assistance: false,
  gated_campus: false,
  security: false,
  sewerage: false,
  park_play_area: false,
  clubhouse: false,
  gym: false,
  swimming_pool: false,
  kids_play_area: false,
  garden: false,
  jogging_track: false,
  indoor_games: false,
  community_hall: false,
  intercom: false,
  water_storage: false,
  modular_kitchen: false,
  wardrobes: false,
  false_ceiling: false,
  geyser: false,
  fans_lights: false,
  covered_parking: false,
  open_parking: false,
  lift_available: false,
  security_24x7: false,
  cctv: false,
  fire_safety: false,
  pantry: false,
  goods_lift: false,
  loading_unloading_area: false,
  
  // Media fields
  main_image: '',
  gallery_images: [],
  videos: [],
  video_urls: [],
  youtube_url: '',
  brochure_file: '',

  // Field visibility (overrides category defaults)
  field_visibility: {},

  // Share preview fields
  share_preview_image: '',
  share_preview_title: '',
  share_preview_description: '',
  
  // SEO / Meta fields
  short_description: '',
  short_description_hi: '',
  full_address_hi: '',
  meta_title: '',
  meta_description: '',
  featured: false,
  map_link: '',
  
  // Legal fields
  rera_registered: true,
  rera_number: 'CGRERA100518A000114',
  
  // Contact fields
  owner_type: 'Builder',
  contact_person: '',
  contact_phone: '',
  contact_email: '',
};

export default function PropertyForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const { categories: dynCategories } = useSiteData();
  const categoryNames = dynCategories?.length ? dynCategories.map((c) => c.name_en) : FALLBACK_CATEGORIES;
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(!!id);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingMain, setUploadingMain] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [uploadingBrochure, setUploadingBrochure] = useState(false);

  const isEditing = !!id;

  useEffect(() => {
    if (id) {
      fetchProperty();
    }
  }, [id]);

  const fetchProperty = async () => {
    try {
      const response = await axios.get(`${API}/properties/${id}`);
      const prop = response.data;
      setFormData({
        ...initialFormData,
        ...prop,
        rate_per_sqft: prop.rate_per_sqft || '',
        gov_rate: prop.gov_rate || '',
        total_price: prop.total_price || '',
        plot_area_sqft: prop.plot_area_sqft || '',
        house_area_sqft: prop.house_area_sqft || '',
        gallery_images: prop.gallery_images || [],
        videos: prop.videos || [],
        video_urls: prop.video_urls || [],
        field_visibility: prop.field_visibility || {},
      });
    } catch (error) {
      toast.error('Failed to load property');
      navigate('/dashboard/properties');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? (value === '' ? '' : parseFloat(value)) : value
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSwitchChange = (name, checked) => {
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  const handleMainImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingMain(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await axios.post(`${API}/upload/image`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setFormData(prev => ({ ...prev, main_image: response.data.url }));
      toast.success('Image uploaded');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingMain(false);
    }
  };

  const handleGalleryImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingGallery(true);
    try {
      const uploadPromises = files.map(async (file) => {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        const response = await axios.post(`${API}/upload/image`, formDataUpload, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        return response.data.url;
      });
      
      const urls = await Promise.all(uploadPromises);
      setFormData(prev => ({ 
        ...prev, 
        gallery_images: [...prev.gallery_images, ...urls] 
      }));
      toast.success(`${urls.length} image(s) uploaded`);
    } catch (error) {
      toast.error('Failed to upload images');
    } finally {
      setUploadingGallery(false);
    }
  };

  const handleBrochureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingBrochure(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await axios.post(`${API}/upload/file`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setFormData(prev => ({ ...prev, brochure_file: response.data.url }));
      toast.success('Brochure uploaded');
    } catch (error) {
      toast.error('Failed to upload brochure');
    } finally {
      setUploadingBrochure(false);
    }
  };

  const removeGalleryImage = (index) => {
    setFormData(prev => ({
      ...prev,
      gallery_images: prev.gallery_images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.code || !formData.category) {
      toast.error('Please fill in required fields');
      return;
    }

    setSubmitting(true);
    try {
      // Clean up data - convert empty strings to null for numeric fields
      const submitData = {
        ...formData,
        rate_per_sqft: formData.rate_per_sqft || null,
        gov_rate: formData.gov_rate || null,
        total_price: formData.total_price || null,
        plot_area_sqft: formData.plot_area_sqft || null,
        house_area_sqft: formData.house_area_sqft || null,
        carpet_area_sqft: formData.carpet_area_sqft || null,
        builtup_area_sqft: formData.builtup_area_sqft || null,
        super_builtup_area_sqft: formData.super_builtup_area_sqft || null,
        latitude: formData.latitude || null,
        longitude: formData.longitude || null,
        road_width_ft: formData.road_width_ft || null,
        bathrooms: formData.bathrooms || null,
        balconies: formData.balconies || null,
        floor_number: formData.floor_number || null,
        total_floors: formData.total_floors || null,
        property_age_years: formData.property_age_years || null,
        ac_count: formData.ac_count || null,
        covered_parking: formData.covered_parking || null,
        open_parking: formData.open_parking || null,
        maintenance_per_month: formData.maintenance_per_month || null,
        frontage_ft: formData.frontage_ft || null,
        ceiling_height_ft: formData.ceiling_height_ft || null,
        monthly_rent: formData.monthly_rent || null,
        security_deposit: formData.security_deposit || null,
        lock_in_period_months: formData.lock_in_period_months || null,
        annual_rent_increase_percent: formData.annual_rent_increase_percent || null,
        lease_term_years: formData.lease_term_years || null,
        washrooms_private: formData.washrooms_private || null,
        washrooms_common: formData.washrooms_common || null,
        booking_amount: formData.booking_amount || null,
      };

      // Strip empty Hindi fields so backend GPT-5.2 auto-translates them
      ['title_hi', 'short_description_hi', 'full_address_hi'].forEach((k) => {
        if (!submitData[k]) delete submitData[k];
      });

      if (isEditing) {
        await axios.put(`${API}/properties/${id}`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Property updated successfully');
      } else {
        await axios.post(`${API}/properties`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Property created successfully');
      }
      navigate('/dashboard/properties');
    } catch (error) {
      toast.error(isEditing ? 'Failed to update property' : 'Failed to create property');
    } finally {
      setSubmitting(false);
    }
  };

  const renderAmenitySection = (title, amenities) => (
    <div className="space-y-3">
      <h4 className="font-medium text-sm text-gray-700">{title}</h4>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {amenities.map((amenity) => (
          <div key={amenity.key} className="flex items-center space-x-2">
            <Switch
              id={amenity.key}
              checked={formData[amenity.key] || false}
              onCheckedChange={(checked) => handleSwitchChange(amenity.key, checked)}
              data-testid={`amenity-${amenity.key}`}
            />
            <Label htmlFor={amenity.key} className="text-sm">{amenity.label}</Label>
          </div>
        ))}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-brand-orange" />
      </div>
    );
  }

  return (
    <div data-testid="property-form">
      <div className="flex items-center space-x-4 mb-6">
        <Button variant="ghost" onClick={() => navigate('/dashboard/properties')} data-testid="back-btn">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div>
          <h1 className="font-heading text-2xl font-bold text-gray-900">
            {isEditing ? 'Edit Property' : 'Add New Property'}
          </h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="grid md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="e.g., Premium Plot in Shivpuri"
                required
                data-testid="title-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="code">Property Code *</Label>
              <Input
                id="code"
                name="code"
                value={formData.code}
                onChange={handleChange}
                placeholder="e.g., K9, H5"
                required
                data-testid="code-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Category *</Label>
              <Select value={formData.category} onValueChange={(v) => handleSelectChange('category', v)}>
                <SelectTrigger data-testid="category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categoryNames.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={formData.status} onValueChange={(v) => handleSelectChange('status', v)}>
                <SelectTrigger data-testid="status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statuses.map((stat) => (
                    <SelectItem key={stat} value={stat}>{stat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Listing Type</Label>
              <Select value={formData.listing_type} onValueChange={(v) => handleSelectChange('listing_type', v)}>
                <SelectTrigger data-testid="listing-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {listingTypes.map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Property Type</Label>
              <Select value={formData.property_type || ''} onValueChange={(v) => handleSelectChange('property_type', v)}>
                <SelectTrigger data-testid="property-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {propertyTypes.map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Transaction Type</Label>
              <Select value={formData.transaction_type} onValueChange={(v) => handleSelectChange('transaction_type', v)}>
                <SelectTrigger data-testid="transaction-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {transactionTypes.map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2 flex items-center space-x-4">
              <Switch
                id="featured"
                checked={formData.featured}
                onCheckedChange={(checked) => handleSwitchChange('featured', checked)}
                data-testid="featured-switch"
              />
              <Label htmlFor="featured">Featured Property (shown on homepage)</Label>
            </div>
          </CardContent>
        </Card>

        {/* Location */}
        <Card>
          <CardHeader>
            <CardTitle>Location Details</CardTitle>
          </CardHeader>
          <CardContent className="grid md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Location Area</Label>
              <Select value={formData.location_label} onValueChange={(v) => handleSelectChange('location_label', v)}>
                <SelectTrigger data-testid="location-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((loc) => (
                    <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="city">City</Label>
              <Input
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="e.g., Bhilai"
                data-testid="city-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="locality">Locality</Label>
              <Input
                id="locality"
                name="locality"
                value={formData.locality}
                onChange={handleChange}
                placeholder="e.g., Shivpuri Colony"
                data-testid="locality-input"
              />
            </div>
            <div className="md:col-span-2 space-y-2">
              <Label htmlFor="full_address">Full Address</Label>
              <Input
                id="full_address"
                name="full_address"
                value={formData.full_address}
                onChange={handleChange}
                placeholder="Complete address"
                data-testid="address-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pincode">Pincode</Label>
              <Input
                id="pincode"
                name="pincode"
                value={formData.pincode}
                onChange={handleChange}
                placeholder="e.g., 490024"
                data-testid="pincode-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="landmark">Landmark</Label>
              <Input
                id="landmark"
                name="landmark"
                value={formData.landmark}
                onChange={handleChange}
                placeholder="e.g., Near XYZ School"
                data-testid="landmark-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="latitude">Latitude</Label>
              <Input
                id="latitude"
                name="latitude"
                type="number"
                step="any"
                value={formData.latitude}
                onChange={handleChange}
                placeholder="e.g., 21.2022"
                data-testid="latitude-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="longitude">Longitude</Label>
              <Input
                id="longitude"
                name="longitude"
                type="number"
                step="any"
                value={formData.longitude}
                onChange={handleChange}
                placeholder="e.g., 81.3406"
                data-testid="longitude-input"
              />
            </div>
            <div className="md:col-span-3 space-y-2">
              <Label htmlFor="google_map_link">Google Maps Link</Label>
              <Input
                id="google_map_link"
                name="google_map_link"
                value={formData.google_map_link}
                onChange={handleChange}
                placeholder="https://maps.app.goo.gl/..."
                data-testid="google-map-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Pricing */}
        <Card>
          <CardHeader>
            <CardTitle>Pricing</CardTitle>
          </CardHeader>
          <CardContent className="grid md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="rate_per_sqft">Rate per Sq Ft (₹)</Label>
              <Input
                id="rate_per_sqft"
                name="rate_per_sqft"
                type="number"
                value={formData.rate_per_sqft}
                onChange={handleChange}
                placeholder="e.g., 550"
                data-testid="rate-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="gov_rate">Government Rate (₹)</Label>
              <Input
                id="gov_rate"
                name="gov_rate"
                type="number"
                value={formData.gov_rate}
                onChange={handleChange}
                placeholder="e.g., 400"
                data-testid="gov-rate-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="total_price">Total Price (₹)</Label>
              <Input
                id="total_price"
                name="total_price"
                type="number"
                value={formData.total_price}
                onChange={handleChange}
                placeholder="e.g., 1649000"
                data-testid="total-price-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="booking_amount">Booking Amount (₹)</Label>
              <Input
                id="booking_amount"
                name="booking_amount"
                type="number"
                value={formData.booking_amount}
                onChange={handleChange}
                placeholder="e.g., 50000"
                data-testid="booking-amount-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pricing_note">Pricing Note</Label>
              <Input
                id="pricing_note"
                name="pricing_note"
                value={formData.pricing_note}
                onChange={handleChange}
                placeholder="e.g., Easy instalment scheme available"
                data-testid="pricing-note-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="payment_plan">Payment Plan</Label>
              <Input
                id="payment_plan"
                name="payment_plan"
                value={formData.payment_plan}
                onChange={handleChange}
                placeholder="e.g., 30% booking, 70% on registry"
                data-testid="payment-plan-input"
              />
            </div>
            <div className="flex items-center space-x-4">
              <Switch
                id="negotiable"
                checked={formData.negotiable}
                onCheckedChange={(checked) => handleSwitchChange('negotiable', checked)}
                data-testid="negotiable-switch"
              />
              <Label htmlFor="negotiable">Price Negotiable</Label>
            </div>
            <div className="flex items-center space-x-4">
              <Switch
                id="loan_possible"
                checked={formData.loan_possible}
                onCheckedChange={(checked) => handleSwitchChange('loan_possible', checked)}
                data-testid="loan-possible-switch"
              />
              <Label htmlFor="loan_possible">Loan Possible</Label>
            </div>
          </CardContent>
        </Card>

        {/* Plot Specifications (show if category is Plot) */}
        {formData.category === 'Plot' && (
          <Card>
            <CardHeader>
              <CardTitle>Plot Specifications</CardTitle>
            </CardHeader>
            <CardContent className="grid md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="plot_area_sqft">Plot Area (sq ft)</Label>
                <Input
                  id="plot_area_sqft"
                  name="plot_area_sqft"
                  type="number"
                  value={formData.plot_area_sqft}
                  onChange={handleChange}
                  placeholder="e.g., 1200"
                  data-testid="plot-area-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Plot Category</Label>
                <Select value={formData.plot_category || ''} onValueChange={(v) => handleSelectChange('plot_category', v)}>
                  <SelectTrigger data-testid="plot-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {plotCategories.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Diversion Type</Label>
                <Select value={formData.diversion_type} onValueChange={(v) => handleSelectChange('diversion_type', v)}>
                  <SelectTrigger data-testid="diversion-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {diversionTypes.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="road_width_ft">Road Width (ft)</Label>
                <Input
                  id="road_width_ft"
                  name="road_width_ft"
                  type="number"
                  value={formData.road_width_ft}
                  onChange={handleChange}
                  placeholder="e.g., 30"
                  data-testid="road-width-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Plot Facing</Label>
                <Select value={formData.plot_facing || ''} onValueChange={(v) => handleSelectChange('plot_facing', v)}>
                  <SelectTrigger data-testid="facing-select">
                    <SelectValue placeholder="Select facing" />
                  </SelectTrigger>
                  <SelectContent>
                    {facingOptions.map((facing) => (
                      <SelectItem key={facing} value={facing}>{facing}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="plot_number">Plot Number</Label>
                <Input
                  id="plot_number"
                  name="plot_number"
                  value={formData.plot_number}
                  onChange={handleChange}
                  placeholder="e.g., K-9"
                  data-testid="plot-number-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sector_block">Sector/Block</Label>
                <Input
                  id="sector_block"
                  name="sector_block"
                  value={formData.sector_block}
                  onChange={handleChange}
                  placeholder="e.g., Sector A"
                  data-testid="sector-input"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* House Specifications (show if category is House) */}
        {formData.category === 'House' && (
          <Card>
            <CardHeader>
              <CardTitle>House Specifications</CardTitle>
            </CardHeader>
            <CardContent className="grid md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="house_area_sqft">Total Area (sq ft)</Label>
                <Input
                  id="house_area_sqft"
                  name="house_area_sqft"
                  type="number"
                  value={formData.house_area_sqft}
                  onChange={handleChange}
                  placeholder="e.g., 1200"
                  data-testid="house-area-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="carpet_area_sqft">Carpet Area (sq ft)</Label>
                <Input
                  id="carpet_area_sqft"
                  name="carpet_area_sqft"
                  type="number"
                  value={formData.carpet_area_sqft}
                  onChange={handleChange}
                  placeholder="e.g., 900"
                  data-testid="carpet-area-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="builtup_area_sqft">Built-up Area (sq ft)</Label>
                <Input
                  id="builtup_area_sqft"
                  name="builtup_area_sqft"
                  type="number"
                  value={formData.builtup_area_sqft}
                  onChange={handleChange}
                  placeholder="e.g., 1000"
                  data-testid="builtup-area-input"
                />
              </div>
              <div className="space-y-2">
                <Label>BHK</Label>
                <Select value={formData.bhk} onValueChange={(v) => handleSelectChange('bhk', v)}>
                  <SelectTrigger data-testid="bhk-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {bhkOptions.map((bhk) => (
                      <SelectItem key={bhk} value={bhk}>{bhk}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="bathrooms">Bathrooms</Label>
                <Input
                  id="bathrooms"
                  name="bathrooms"
                  type="number"
                  value={formData.bathrooms}
                  onChange={handleChange}
                  placeholder="e.g., 2"
                  data-testid="bathrooms-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="balconies">Balconies</Label>
                <Input
                  id="balconies"
                  name="balconies"
                  type="number"
                  value={formData.balconies}
                  onChange={handleChange}
                  placeholder="e.g., 1"
                  data-testid="balconies-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="floor_info">Floor Info</Label>
                <Input
                  id="floor_info"
                  name="floor_info"
                  value={formData.floor_info}
                  onChange={handleChange}
                  placeholder="e.g., Ground + 1st Floor"
                  data-testid="floor-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="total_floors">Total Floors</Label>
                <Input
                  id="total_floors"
                  name="total_floors"
                  type="number"
                  value={formData.total_floors}
                  onChange={handleChange}
                  placeholder="e.g., 2"
                  data-testid="total-floors-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="property_age_years">Property Age (years)</Label>
                <Input
                  id="property_age_years"
                  name="property_age_years"
                  type="number"
                  value={formData.property_age_years}
                  onChange={handleChange}
                  placeholder="e.g., 0 for new"
                  data-testid="property-age-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Construction Status</Label>
                <Select value={formData.construction_status || ''} onValueChange={(v) => handleSelectChange('construction_status', v)}>
                  <SelectTrigger data-testid="construction-status-select">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {constructionStatuses.map((status) => (
                      <SelectItem key={status} value={status}>{status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Furnishing Status</Label>
                <Select value={formData.furnishing_status || ''} onValueChange={(v) => handleSelectChange('furnishing_status', v)}>
                  <SelectTrigger data-testid="furnishing-status-select">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {furnishingStatuses.map((status) => (
                      <SelectItem key={status} value={status}>{status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="flooring_type">Flooring Type</Label>
                <Input
                  id="flooring_type"
                  name="flooring_type"
                  value={formData.flooring_type}
                  onChange={handleChange}
                  placeholder="e.g., Vitrified Tiles"
                  data-testid="flooring-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Water Source</Label>
                <Select value={formData.water_source || ''} onValueChange={(v) => handleSelectChange('water_source', v)}>
                  <SelectTrigger data-testid="water-source-select">
                    <SelectValue placeholder="Select source" />
                  </SelectTrigger>
                  <SelectContent>
                    {waterSources.map((source) => (
                      <SelectItem key={source} value={source}>{source}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Power Backup</Label>
                <Select value={formData.power_backup || ''} onValueChange={(v) => handleSelectChange('power_backup', v)}>
                  <SelectTrigger data-testid="power-backup-select">
                    <SelectValue placeholder="Select option" />
                  </SelectTrigger>
                  <SelectContent>
                    {powerBackupOptions.map((option) => (
                      <SelectItem key={option} value={option}>{option}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="maintenance_per_month">Maintenance/Month (₹)</Label>
                <Input
                  id="maintenance_per_month"
                  name="maintenance_per_month"
                  type="number"
                  value={formData.maintenance_per_month}
                  onChange={handleChange}
                  placeholder="e.g., 2000"
                  data-testid="maintenance-input"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Commercial Specifications (show if category is Commercial) */}
        {formData.category === 'Commercial' && (
          <Card>
            <CardHeader>
              <CardTitle>Commercial Specifications</CardTitle>
            </CardHeader>
            <CardContent className="grid md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Commercial Type</Label>
                <Select value={formData.commercial_type || ''} onValueChange={(v) => handleSelectChange('commercial_type', v)}>
                  <SelectTrigger data-testid="commercial-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {commercialTypes.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="carpet_area_sqft">Carpet Area (sq ft)</Label>
                <Input
                  id="carpet_area_sqft"
                  name="carpet_area_sqft"
                  type="number"
                  value={formData.carpet_area_sqft}
                  onChange={handleChange}
                  placeholder="e.g., 500"
                  data-testid="commercial-carpet-area-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="frontage_ft">Frontage (ft)</Label>
                <Input
                  id="frontage_ft"
                  name="frontage_ft"
                  type="number"
                  value={formData.frontage_ft}
                  onChange={handleChange}
                  placeholder="e.g., 20"
                  data-testid="frontage-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ceiling_height_ft">Ceiling Height (ft)</Label>
                <Input
                  id="ceiling_height_ft"
                  name="ceiling_height_ft"
                  type="number"
                  value={formData.ceiling_height_ft}
                  onChange={handleChange}
                  placeholder="e.g., 12"
                  data-testid="ceiling-height-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Zone Type</Label>
                <Select value={formData.zone_type || ''} onValueChange={(v) => handleSelectChange('zone_type', v)}>
                  <SelectTrigger data-testid="zone-type-select">
                    <SelectValue placeholder="Select zone" />
                  </SelectTrigger>
                  <SelectContent>
                    {zoneTypes.map((zone) => (
                      <SelectItem key={zone} value={zone}>{zone}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Signage Visibility</Label>
                <Select value={formData.signage_visibility || ''} onValueChange={(v) => handleSelectChange('signage_visibility', v)}>
                  <SelectTrigger data-testid="signage-select">
                    <SelectValue placeholder="Select visibility" />
                  </SelectTrigger>
                  <SelectContent>
                    {signageOptions.map((option) => (
                      <SelectItem key={option} value={option}>{option}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="washrooms_private">Private Washrooms</Label>
                <Input
                  id="washrooms_private"
                  name="washrooms_private"
                  type="number"
                  value={formData.washrooms_private}
                  onChange={handleChange}
                  placeholder="e.g., 1"
                  data-testid="washrooms-private-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="washrooms_common">Common Washrooms</Label>
                <Input
                  id="washrooms_common"
                  name="washrooms_common"
                  type="number"
                  value={formData.washrooms_common}
                  onChange={handleChange}
                  placeholder="e.g., 2"
                  data-testid="washrooms-common-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="possession_status">Possession Status</Label>
                <Input
                  id="possession_status"
                  name="possession_status"
                  value={formData.possession_status}
                  onChange={handleChange}
                  placeholder="e.g., Immediate"
                  data-testid="possession-input"
                />
              </div>
              
              {/* Rent/Lease specific fields */}
              {(formData.listing_type === 'Rent' || formData.listing_type === 'Lease') && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="monthly_rent">Monthly Rent (₹)</Label>
                    <Input
                      id="monthly_rent"
                      name="monthly_rent"
                      type="number"
                      value={formData.monthly_rent}
                      onChange={handleChange}
                      placeholder="e.g., 25000"
                      data-testid="monthly-rent-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="security_deposit">Security Deposit (₹)</Label>
                    <Input
                      id="security_deposit"
                      name="security_deposit"
                      type="number"
                      value={formData.security_deposit}
                      onChange={handleChange}
                      placeholder="e.g., 100000"
                      data-testid="security-deposit-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lock_in_period_months">Lock-in Period (months)</Label>
                    <Input
                      id="lock_in_period_months"
                      name="lock_in_period_months"
                      type="number"
                      value={formData.lock_in_period_months}
                      onChange={handleChange}
                      placeholder="e.g., 11"
                      data-testid="lockin-input"
                    />
                  </div>
                  <div className="flex items-center space-x-4">
                    <Switch
                      id="rent_negotiable"
                      checked={formData.rent_negotiable}
                      onCheckedChange={(checked) => handleSwitchChange('rent_negotiable', checked)}
                      data-testid="rent-negotiable-switch"
                    />
                    <Label htmlFor="rent_negotiable">Rent Negotiable</Label>
                  </div>
                  <div className="flex items-center space-x-4">
                    <Switch
                      id="maintenance_included"
                      checked={formData.maintenance_included}
                      onCheckedChange={(checked) => handleSwitchChange('maintenance_included', checked)}
                      data-testid="maintenance-included-switch"
                    />
                    <Label htmlFor="maintenance_included">Maintenance Included</Label>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Amenities */}
        <Card>
          <CardHeader>
            <CardTitle>Amenities</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {renderAmenitySection('Basic Infrastructure', basicAmenities)}
            {renderAmenitySection('Location Advantages', locationAmenities)}
            {renderAmenitySection('Security Features', securityAmenities)}
            {formData.category !== 'Plot' && renderAmenitySection('Premium Amenities', premiumAmenities)}
            {formData.category === 'House' && renderAmenitySection('House Features', houseAmenities)}
            {formData.category === 'Commercial' && renderAmenitySection('Commercial Features', commercialAmenities)}
          </CardContent>
        </Card>

        {/* Media */}
        <Card>
          <CardHeader>
            <CardTitle>Media & Resources</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Main Image */}
            <div className="space-y-2">
              <Label>Main Image</Label>
              <div className="flex items-start space-x-4">
                {formData.main_image && (
                  <div className="relative">
                    <img src={formData.main_image} alt="Main" className="w-32 h-32 object-cover rounded-lg" />
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, main_image: '' }))}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}
                <label className="flex flex-col items-center justify-center w-32 h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleMainImageUpload}
                    className="hidden"
                    data-testid="main-image-input"
                  />
                  {uploadingMain ? (
                    <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                  ) : (
                    <>
                      <Upload className="h-6 w-6 text-gray-400 mb-2" />
                      <span className="text-xs text-gray-500">Upload</span>
                    </>
                  )}
                </label>
              </div>
            </div>

            {/* Gallery Images */}
            <div className="space-y-2">
              <Label>Gallery Images</Label>
              <div className="flex flex-wrap gap-4">
                {formData.gallery_images.map((img, index) => (
                  <div key={index} className="relative">
                    <img src={img} alt={`Gallery ${index}`} className="w-24 h-24 object-cover rounded-lg" />
                    <button
                      type="button"
                      onClick={() => removeGalleryImage(index)}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                <label className="flex flex-col items-center justify-center w-24 h-24 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleGalleryImageUpload}
                    className="hidden"
                    data-testid="gallery-image-input"
                  />
                  {uploadingGallery ? (
                    <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                  ) : (
                    <>
                      <Plus className="h-5 w-5 text-gray-400" />
                      <span className="text-xs text-gray-500">Add</span>
                    </>
                  )}
                </label>
              </div>
            </div>

            {/* YouTube, Map Link, Brochure */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="youtube_url">YouTube Video URL</Label>
                <Input
                  id="youtube_url"
                  name="youtube_url"
                  value={formData.youtube_url}
                  onChange={handleChange}
                  placeholder="https://youtube.com/watch?v=..."
                  data-testid="youtube-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="map_link">Alternate Map Link</Label>
                <Input
                  id="map_link"
                  name="map_link"
                  value={formData.map_link}
                  onChange={handleChange}
                  placeholder="https://maps.google.com/..."
                  data-testid="map-input"
                />
              </div>
            </div>

            {/* Brochure Upload */}
            <div className="space-y-2">
              <Label>Brochure (PDF) — Inquiry-gated download</Label>
              <MediaUpload
                kind="document"
                value={formData.brochure_file}
                onChange={(v) => setFormData((p) => ({ ...p, brochure_file: v }))}
                testId="brochure-upload"
              />
              <p className="text-xs text-gray-500">Visitors must submit an inquiry form before the brochure URL is revealed.</p>
            </div>

            {/* Uploaded videos */}
            <div className="space-y-2">
              <Label>Property Videos (uploaded files)</Label>
              <MediaUpload
                kind="video"
                multiple
                value={formData.videos}
                onChange={(v) => setFormData((p) => ({ ...p, videos: v }))}
                maxSizeMB={200}
                testId="videos-upload"
              />
            </div>

            {/* External video links */}
            <div className="space-y-2">
              <Label>Additional Video Links (YouTube / Vimeo, one per line)</Label>
              <Textarea
                rows={3}
                value={(formData.video_urls || []).join('\n')}
                onChange={(e) => setFormData((p) => ({ ...p, video_urls: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean) }))}
                placeholder="https://youtube.com/watch?v=...\nhttps://vimeo.com/..."
              />
            </div>
          </CardContent>
        </Card>

        {/* Field Visibility */}
        <Card>
          <CardHeader>
            <CardTitle>Public Display Visibility</CardTitle>
            <p className="text-sm text-gray-500">Override category defaults for this listing only. Leave a switch in its default state to inherit from the category.</p>
          </CardHeader>
          <CardContent className="grid gap-3 md:grid-cols-2">
            {VISIBILITY_FIELDS.map((f) => (
              <div key={f.key} className="flex items-center justify-between border rounded-lg px-3 py-2">
                <Label htmlFor={`vis-${f.key}`}>{f.label}</Label>
                <Switch
                  id={`vis-${f.key}`}
                  checked={formData.field_visibility?.[f.key] !== false}
                  onCheckedChange={(v) => setFormData((p) => ({ ...p, field_visibility: { ...(p.field_visibility || {}), [f.key]: v } }))}
                  data-testid={`prop-vis-${f.key}`}
                />
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Social Share Preview */}
        <Card>
          <CardHeader>
            <CardTitle>Social Media Share Preview</CardTitle>
          </CardHeader>
          <CardContent className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="share_preview_title">Share Title</Label>
              <Input
                id="share_preview_title"
                name="share_preview_title"
                value={formData.share_preview_title}
                onChange={handleChange}
                placeholder="Custom title for social sharing"
                data-testid="share-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="share_preview_image">Share Image URL</Label>
              <Input
                id="share_preview_image"
                name="share_preview_image"
                value={formData.share_preview_image}
                onChange={handleChange}
                placeholder="URL for social media preview image"
                data-testid="share-image-input"
              />
            </div>
            <div className="md:col-span-2 space-y-2">
              <Label htmlFor="share_preview_description">Share Description</Label>
              <Textarea
                id="share_preview_description"
                name="share_preview_description"
                value={formData.share_preview_description}
                onChange={handleChange}
                placeholder="Custom description for WhatsApp/Facebook sharing"
                rows={2}
                data-testid="share-description-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Description & SEO */}
        <Card>
          <CardHeader>
            <CardTitle>Description & SEO</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="short_description">Short Description</Label>
              <Textarea
                id="short_description"
                name="short_description"
                value={formData.short_description}
                onChange={handleChange}
                placeholder="Brief description of the property..."
                rows={4}
                data-testid="description-input"
              />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="meta_title">Meta Title</Label>
                <Input
                  id="meta_title"
                  name="meta_title"
                  value={formData.meta_title}
                  onChange={handleChange}
                  placeholder="SEO title"
                  data-testid="meta-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="meta_description">Meta Description</Label>
                <Input
                  id="meta_description"
                  name="meta_description"
                  value={formData.meta_description}
                  onChange={handleChange}
                  placeholder="SEO description"
                  data-testid="meta-desc-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Hindi Translation Overrides */}
        <Card className="border-orange-200 bg-orange-50/30">
          <CardHeader>
            <CardTitle className="text-base">Hindi Version (Optional Override)</CardTitle>
            <p className="text-xs text-gray-600">Leave blank — GPT-5.2 auto-translates English title, description and address into natural real-estate-tone Hindi when you save.</p>
          </CardHeader>
          <CardContent className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="title_hi">शीर्षक (Hindi Title)</Label>
              <Input
                id="title_hi"
                name="title_hi"
                value={formData.title_hi || ''}
                onChange={handleChange}
                dir="auto"
                placeholder="Auto-generated by AI if left empty"
                data-testid="title-hi-input"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="short_description_hi">विवरण (Hindi Description)</Label>
              <Textarea
                id="short_description_hi"
                name="short_description_hi"
                value={formData.short_description_hi || ''}
                onChange={handleChange}
                rows={3}
                dir="auto"
                placeholder="Auto-generated by AI if left empty"
                data-testid="description-hi-input"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="full_address_hi">पता (Hindi Address)</Label>
              <Input
                id="full_address_hi"
                name="full_address_hi"
                value={formData.full_address_hi || ''}
                onChange={handleChange}
                dir="auto"
                placeholder="Auto-generated by AI if left empty"
                data-testid="address-hi-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Legal & Contact */}
        <Card>
          <CardHeader>
            <CardTitle>Legal & Contact Info</CardTitle>
          </CardHeader>
          <CardContent className="grid md:grid-cols-3 gap-4">
            <div className="flex items-center space-x-4">
              <Switch
                id="rera_registered"
                checked={formData.rera_registered}
                onCheckedChange={(checked) => handleSwitchChange('rera_registered', checked)}
                data-testid="rera-switch"
              />
              <Label htmlFor="rera_registered">RERA Registered</Label>
            </div>
            <div className="space-y-2">
              <Label htmlFor="rera_number">RERA Number</Label>
              <Input
                id="rera_number"
                name="rera_number"
                value={formData.rera_number}
                onChange={handleChange}
                placeholder="RERA registration number"
                data-testid="rera-number-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Owner Type</Label>
              <Select value={formData.owner_type} onValueChange={(v) => handleSelectChange('owner_type', v)}>
                <SelectTrigger data-testid="owner-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ownerTypes.map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact_person">Contact Person</Label>
              <Input
                id="contact_person"
                name="contact_person"
                value={formData.contact_person}
                onChange={handleChange}
                placeholder="Name of contact person"
                data-testid="contact-person-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact_phone">Contact Phone</Label>
              <Input
                id="contact_phone"
                name="contact_phone"
                value={formData.contact_phone}
                onChange={handleChange}
                placeholder="Phone number"
                data-testid="contact-phone-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact_email">Contact Email</Label>
              <Input
                id="contact_email"
                name="contact_email"
                type="email"
                value={formData.contact_email}
                onChange={handleChange}
                placeholder="Email address"
                data-testid="contact-email-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/dashboard/properties')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={submitting}
            className="bg-gradient-to-r from-brand-orange to-brand-rust text-white"
            data-testid="submit-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {isEditing ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              isEditing ? 'Update Property' : 'Create Property'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
